import { useRef, useState } from 'react'
import { PiCheck, PiCircleNotch, PiFilePdf, PiPlus } from 'react-icons/pi'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'
import { Popover, PopoverAnchor, PopoverContent } from 'components/UI/popover'
import { Ds2026SearchInput } from 'components/FbUI/Ds2026SearchInput'
import FbButton from 'components/FbUI/FbButton'
import { Skeleton } from 'components/FbUI/Skeleton'
import { NewAttachment } from 'components/Attachments/AttachmentList'
import { cn } from 'components/UI/cn'
import { VariantCatalog, useListVariantsCatalog } from 'services/products'
import { buildSpecSheetAttachment, isSpecSheetAttached } from './specSheets'

interface SpecSheetPickerProps {
  /**
   * Called with a generated-PDF attachment when a spec sheet is picked.
   * Returns whether the attachment was accepted (the parent runs it through
   * the shared size/type validation); the picker only closes on acceptance.
   */
  onAttach: (attachment: NewAttachment) => boolean
  /** Filenames already attached; those rows show as attached and are disabled. */
  attachedFilenames?: string[]
  disabled?: boolean
}

/** One row's secondary line: brand, pack/size, and SKU, whichever exist. */
function specSheetSubtitle(variant: VariantCatalog): string {
  return [
    variant.syndigo_product?.brand_name || variant.brand_name,
    variant.format_pack_size,
    variant.sku,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * DS2026 "Attach spec sheet" affordance. Renders a compact toolbar trigger
 * that opens a searchable popover of the company's Syndigo-linked variants;
 * selecting one fetches its generated spec-sheet PDF and attaches it to the
 * composing email. Renders nothing for tenants with no Syndigo products, so
 * companies without synced product data never see a dead trigger.
 */
export function SpecSheetPicker({
  onAttach,
  attachedFilenames = [],
  disabled = false,
}: SpecSheetPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [attachingId, setAttachingId] = useState<number | null>(null)
  // Synchronous mirror of `attachingId` for the double-click guard. State
  // reads in `handlePick` come from the render closure, so two clicks in the
  // same tick both see null; the ref updates immediately and closes that gap.
  const attachingRef = useRef(false)
  const [debouncedQuery] = useDebounce(query.trim(), 300)

  // Cheap availability probe: does this tenant have ANY spec-sheet-able
  // (Syndigo-linked) variant? Cached by react-query across modal opens.
  const { data: probe } = useListVariantsCatalog({
    source: 'syndigo',
    page_size: 1,
  })

  const { data, isLoading, error } = useListVariantsCatalog(
    {
      source: 'syndigo',
      search: debouncedQuery || undefined,
      page_size: 25,
      sort: 'name',
    },
    { query: { enabled: open } }
  )
  const sheets = data?.results ?? []

  if (!probe || probe.count === 0) {
    return null
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setQuery('')
  }

  const handlePick = async (variant: VariantCatalog) => {
    // One attach at a time. Rows disable via `attachingId` on re-render, but a
    // fast second click can land before that render commits; the ref is the
    // only check that sees the first click synchronously, so without it a
    // same-tick second click would fetch again and attach a duplicate.
    if (attachingRef.current) return
    attachingRef.current = true
    setAttachingId(variant.id)
    try {
      const attachment = await buildSpecSheetAttachment(variant)
      // The parent validates against the shared attachment limits and shows
      // the standard error toast on rejection; keep the picker open then so
      // the user can pick a smaller sheet or remove other attachments first.
      if (onAttach(attachment)) {
        handleOpenChange(false)
      }
    } catch {
      toast.error('Could not generate that spec sheet. Please try again.')
    } finally {
      attachingRef.current = false
      setAttachingId(null)
    }
  }

  const emptyMessage = debouncedQuery
    ? `No spec sheets match “${debouncedQuery}”.`
    : 'No spec sheets available yet.'

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        {/* The real DS2026 button (secondary = outline under the modal's
            Ds2026ButtonProvider). Anchored, not a PopoverTrigger, so it sits
            outside Quill's `.ql-toolbar` and escapes its button CSS. */}
        <FbButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          aria-label="Attach spec sheet"
          className="whitespace-nowrap"
          onClick={() => setOpen((o) => !o)}
        >
          <PiPlus size={15} />
          Spec sheet
        </FbButton>
      </PopoverAnchor>
      <PopoverContent align="end" sideOffset={6} className="w-80 p-0">
        <div className="border-b border-line px-3 py-2.5">
          <p className="text-sm font-semibold tracking-[-0.2px] text-neutral-800">
            Attach a spec sheet
          </p>
          <p className="text-xs text-neutral-500">
            Generated from product data
          </p>
        </div>
        <div className="px-2.5 pt-2.5">
          <Ds2026SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search products"
            size="sm"
            aria-label="Search spec sheets"
          />
        </div>
        <div className="fbui-scroll max-h-72 overflow-y-auto px-1.5 py-2">
          {error ? (
            <p className="px-2 py-6 text-center text-sm text-neutral-500">
              Could not load spec sheets. Please try again.
            </p>
          ) : isLoading ? (
            // Skeleton rows mirror the real row layout (icon tile + two text
            // lines) so nothing reflows when results land. Per client/CLAUDE.md,
            // prefer a skeleton over text/spinner when the layout is known.
            [0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2"
              >
                <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="mt-1.5 h-3 w-1/2" />
                </div>
              </div>
            ))
          ) : sheets.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-neutral-500">
              {emptyMessage}
            </p>
          ) : (
            sheets.map((variant) => {
              const isAttached = isSpecSheetAttached(variant, attachedFilenames)
              const isAttaching = attachingId === variant.id
              const subtitle = specSheetSubtitle(variant)
              return (
                <button
                  key={variant.id}
                  type="button"
                  disabled={isAttached || attachingId !== null}
                  onClick={() => handlePick(variant)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
                    isAttached
                      ? 'cursor-default opacity-60'
                      : 'hover:bg-neutral-100'
                  )}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-500">
                    {isAttaching ? (
                      <PiCircleNotch size={18} className="animate-spin" />
                    ) : (
                      <PiFilePdf size={18} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium tracking-[-0.2px] text-neutral-800">
                      {variant.name}
                    </span>
                    {subtitle && (
                      <span className="block truncate text-xs text-neutral-500">
                        {subtitle}
                      </span>
                    )}
                  </span>
                  {isAttached && (
                    <PiCheck
                      size={16}
                      className="shrink-0 text-brand-active"
                      aria-label="Attached"
                    />
                  )}
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
